import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  vipUsersAll,
  vipBenefits,
  vipScoreActions,
  vipDashboardStats,
  vipAccessMethods,
  vipVisibility,
  tierConfig,
  getVipUsersByTier,
  getVipUserById,
  getTierPercent,
  formatVipCount,
  type VipUser,
  type VipTier,
} from '@/mocks/rfwVipUsers';

function formatCompact(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function TierBadge({ tier, size = 'sm' }: { tier: VipTier; size?: 'sm' | 'md' }) {
  const tc = tierConfig[tier];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${size === 'md' ? 'text-[10px] px-3 py-1' : 'text-[9px]'} ${tc.badgeBg} ${tc.badgeText}`}>
      {tc.emoji} {tc.label}
    </span>
  );
}

function PodiumVipCard({ user, position }: { user: VipUser; position: number }) {
  const t = tierConfig[user.tier];
  const medals: Record<number, { gradient: string; label: string; border: string }> = {
    1: { gradient: 'from-violet-400 via-purple-500 to-fuchsia-500', label: 'DIAMANTE', border: 'border-violet-400' },
    2: { gradient: 'from-amber-400 to-yellow-500', label: 'ORO', border: 'border-amber-400' },
    3: { gradient: 'from-slate-300 to-slate-500', label: 'PLATA', border: 'border-slate-300' },
  };
  const medal = medals[position];

  return (
    <div className="flex flex-col items-center">
      <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${medal.gradient} text-background-50 text-[10px] font-extrabold tracking-wider mb-2`}>
        {medal.label}
      </div>
      <div className="relative mb-2">
        <img src={user.photoUrl} alt={user.name} className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover object-top border-2 ${medal.border}`} />
        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-r ${medal.gradient} flex items-center justify-center text-background-50 text-xs font-bold`}>
          {position}
        </div>
      </div>
      <span className="text-xs font-bold text-foreground-950 text-center leading-tight">{user.name}</span>
      <span className="text-[10px] text-foreground-500">{user.username}</span>
      <TierBadge tier={user.tier} />
      <div className={`text-lg md:text-xl font-extrabold ${t.color} mt-1`}>{user.score}</div>
      <span className="text-[10px] text-foreground-400">pts VIP</span>
      <div className="mt-2 flex flex-col gap-0.5 items-center">
        <span className="text-[10px] text-foreground-500">{user.puntosTotales.toLocaleString()} pts totales</span>
        <span className="text-[10px] text-foreground-500">{user.actividadDias}d activo</span>
        <span className="text-[10px] text-foreground-500">+{user.scoreAdicionalGenerado} score extra</span>
      </div>
    </div>
  );
}

function VipListCard({ user, position }: { user: VipUser; position: number }) {
  const t = tierConfig[user.tier];
  const isEven = position % 2 === 0;

  return (
    <div className={`flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3.5 cursor-pointer ${isEven ? 'bg-background-50' : 'bg-background-50/70'}`}>
      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
        <span className="text-xs font-bold text-foreground-500">#{position}</span>
      </div>
      <img src={user.photoUrl} alt={user.name} className="w-10 h-10 rounded-lg object-cover object-top shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground-950 truncate">{user.name}</span>
          <span className="text-[10px] text-foreground-500">{user.username}</span>
          <TierBadge tier={user.tier} />
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-flashlight-line text-foreground-400"></i>{user.puntosTotales.toLocaleString()}
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-fire-line text-foreground-400"></i>{user.actividadDias}d
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-mail-send-line text-foreground-400"></i>{user.invitacionesHechas}
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-play-circle-line text-foreground-400"></i>{user.contenidoExclusivoAccedido}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-1">
          {user.accessMethods.map((am) => {
            const method = vipAccessMethods.find((m) => m.type === am);
            if (!method) return null;
            return (
              <span key={am} className={`px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${method.bgColor} ${method.color}`}>
                <i className={`${method.icon} mr-0.5 text-[7px]`}></i>
                {method.label}
              </span>
            );
          })}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-sm font-bold ${t.color}`}>{user.score}</div>
        <div className="text-[10px] text-foreground-400">+{user.scoreAdicionalGenerado}</div>
      </div>
    </div>
  );
}

function TierProgressBar({ user }: { user: VipUser }) {
  const percent = getTierPercent(user);
  const t = tierConfig[user.tier];

  return (
    <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm">{t.emoji}</span>
          <span className={`text-xs font-bold ${t.textColor}`}>{t.label}</span>
        </div>
        <span className="text-[9px] font-medium text-violet-600">{percent}%</span>
      </div>
      <div className="w-full bg-background-200 rounded-full h-2 mb-1">
        <div className={`h-full rounded-full transition-all duration-500 bg-gradient-to-r ${t.gradient}`} style={{ width: `${percent}%` }}></div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-foreground-500">{user.score} / 1000 pts</span>
      </div>
    </div>
  );
}

export default function VipRfwPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activeView, setActiveView] = useState<'ranking' | 'beneficios' | 'acceso'>('ranking');
  const [tierFilter, setTierFilter] = useState<VipTier | 'todos'>('todos');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const filteredUsers = useMemo(() => {
    if (tierFilter === 'todos') return vipUsersAll;
    return getVipUsersByTier(tierFilter);
  }, [tierFilter]);

  const podiumUsers = filteredUsers.slice(0, 3);
  const restUsers = filteredUsers.slice(3);

  const selectedUser = selectedUserId ? getVipUserById(selectedUserId) : null;
  const selectedUserTier = selectedUser ? tierConfig[selectedUser.tier] : null;

  const tabs = [
    { id: 'ranking' as const, label: 'Ranking VIP', icon: 'ri-trophy-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-gift-2-line' },
    { id: 'acceso' as const, label: 'Cómo Ser VIP', icon: 'ri-key-2-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-40 md:h-48 bg-gradient-to-br from-violet-500/20 via-purple-400/15 to-fuchsia-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/30"></div>
              <div className="absolute -top-14 -right-14 w-60 h-60 rounded-full bg-violet-400/10 blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-fuchsia-300/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center mb-3">
                  <i className="ri-vip-diamond-line text-background-50 text-2xl"></i>
                </div>
                <h1 className="text-xl md:text-2xl font-black text-foreground-950 mb-1">Club VIP RFW</h1>
                <p className="text-sm text-foreground-500">{vipDashboardStats.totalVips} VIPs · {vipDashboardStats.diamante} Diamante · {vipDashboardStats.oro} Oro · {vipDashboardStats.plata} Plata</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-background-200">
              {[
                { label: 'VIPs', value: vipDashboardStats.totalVips, icon: 'ri-vip-diamond-line', color: 'text-violet-500' },
                { label: 'Diamante', value: vipDashboardStats.diamante, icon: 'ri-vip-crown-fill', color: 'text-fuchsia-500' },
                { label: 'Oro', value: vipDashboardStats.oro, icon: 'ri-vip-diamond-fill', color: 'text-amber-500' },
                { label: 'Plata', value: vipDashboardStats.plata, icon: 'ri-medal-line', color: 'text-slate-500' },
                { label: 'Score Extra', value: formatCompact(vipDashboardStats.scoreTotalGenerado), icon: 'ri-flashlight-line', color: 'text-emerald-500' },
                { label: 'Suscriptores', value: vipDashboardStats.suscriptores, icon: 'ri-vip-crown-line', color: 'text-rose-500' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* View Tabs */}
          <div className="mt-5 flex items-center justify-center">
            <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeView === tab.id
                      ? 'bg-violet-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${tab.icon} text-[10px]`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Ranking View */}
          {activeView === 'ranking' && (
            <>
              {/* Tier Filters */}
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <button
                  onClick={() => setTierFilter('todos')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    tierFilter === 'todos'
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                  }`}
                >
                  <i className="ri-vip-diamond-line"></i>
                  Todos ({vipDashboardStats.totalVips})
                </button>
                {(['diamante', 'oro', 'plata'] as VipTier[]).map((tier) => {
                  const tc = tierConfig[tier];
                  const count = tier === 'diamante' ? vipDashboardStats.diamante : tier === 'oro' ? vipDashboardStats.oro : vipDashboardStats.plata;
                  return (
                    <button
                      key={tier}
                      onClick={() => setTierFilter(tier)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        tierFilter === tier
                          ? `${tc.badgeBg} ${tc.badgeText} border ${tc.borderColor}`
                          : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      {tc.emoji} {tc.label}
                      <span className="text-[10px] opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>

              {/* User count info */}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-foreground-400">
                  {filteredUsers.length} VIPs · {tierFilter === 'todos' ? 'Todos los niveles' : tierConfig[tierFilter].label}
                </span>
              </div>

              {/* Podium */}
              {podiumUsers.length >= 3 && (
                <div className="mt-6">
                  <div className="flex items-end justify-center gap-3 md:gap-6 px-2">
                    <PodiumVipCard user={podiumUsers[1]} position={2} />
                    <PodiumVipCard user={podiumUsers[0]} position={1} />
                    <PodiumVipCard user={podiumUsers[2]} position={3} />
                  </div>
                </div>
              )}

              {/* Tier Overview Cards */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(['diamante', 'oro', 'plata'] as VipTier[]).map((tier) => {
                  const tc = tierConfig[tier];
                  const count = tier === 'diamante' ? vipDashboardStats.diamante : tier === 'oro' ? vipDashboardStats.oro : vipDashboardStats.plata;
                  const avgScore = Math.round(getVipUsersByTier(tier).reduce((s, u) => s + u.score, 0) / Math.max(count, 1));
                  return (
                    <div key={tier} className={`p-4 rounded-2xl border bg-background-50 ${tc.borderColor} ${tc.ringColor} ring-1`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tc.badgeBg}`}>
                          <span className="text-lg">{tc.emoji}</span>
                        </div>
                        <div>
                          <p className={`text-sm font-bold ${tc.textColor}`}>{tc.label}</p>
                          <p className="text-[10px] text-foreground-500">{count} miembros · avg {avgScore} pts</p>
                        </div>
                      </div>
                      {/* Top user of this tier */}
                      {getVipUsersByTier(tier).slice(0, 1).map((u) => (
                        <div key={u.id} className="flex items-center gap-2 p-2 rounded-lg bg-background-100">
                          <img src={u.photoUrl} alt={u.name} className="w-8 h-8 rounded-full object-cover object-top" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold text-foreground-950 truncate">{u.name}</p>
                            <p className="text-[8px] text-foreground-400">{u.score} pts · {u.actividadDias}d</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Rest of ranking */}
              {restUsers.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-sm font-bold text-foreground-950">Posiciones 4 - {filteredUsers.length}</h3>
                  </div>
                  <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70">
                    {restUsers.map((user, idx) => (
                      <div key={user.id} onClick={() => setSelectedUserId(user.id)}>
                        <VipListCard user={user} position={4 + idx} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* VIP Detail Modal */}
              {selectedUser && selectedUserTier && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40" onClick={() => setSelectedUserId(null)}>
                  <div className="bg-background-50 rounded-2xl p-6 w-[440px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-foreground-950">Perfil VIP</h3>
                      <button onClick={() => setSelectedUserId(null)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                        <i className="ri-close-line text-foreground-500"></i>
                      </button>
                    </div>

                    <div className="text-center mb-4">
                      <img src={selectedUser.photoUrl} alt={selectedUser.name} className="w-20 h-20 rounded-2xl object-cover object-top mx-auto mb-3 border-2 border-violet-200" />
                      <p className="text-base font-bold text-foreground-950">{selectedUser.name}</p>
                      <p className="text-xs text-foreground-500">{selectedUser.username} · {selectedUser.radioFrequencia}</p>
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <TierBadge tier={selectedUser.tier} size="md" />
                        <span className="text-[10px] text-foreground-400">#{vipUsersAll.findIndex((u) => u.id === selectedUser.id) + 1} Global</span>
                      </div>
                    </div>

                    <TierProgressBar user={selectedUser} />

                    {/* Stats Grid */}
                    <div className="mt-4 grid grid-cols-3 gap-3">
                      {[
                        { label: 'Puntos Totales', value: selectedUser.puntosTotales.toLocaleString(), icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
                        { label: 'Días Activo', value: `${selectedUser.actividadDias}d`, icon: 'ri-fire-line', color: 'text-rose-600 bg-rose-50' },
                        { label: 'Invitaciones', value: selectedUser.invitacionesHechas, icon: 'ri-mail-send-line', color: 'text-violet-600 bg-violet-50' },
                        { label: 'Contenido VIP', value: selectedUser.contenidoExclusivoAccedido, icon: 'ri-play-circle-line', color: 'text-rose-600 bg-rose-50' },
                        { label: 'Eventos', value: selectedUser.eventosAsistidos, icon: 'ri-calendar-check-line', color: 'text-amber-600 bg-amber-50' },
                        { label: 'Concursos Ganados', value: selectedUser.concursosGanados, icon: 'ri-trophy-line', color: 'text-orange-600 bg-orange-50' },
                      ].map((stat) => (
                        <div key={stat.label} className="text-center p-3 rounded-xl bg-background-100">
                          <div className={`w-8 h-8 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-1`}>
                            <i className={`${stat.icon} text-sm`}></i>
                          </div>
                          <p className="text-sm font-bold text-foreground-950">{stat.value}</p>
                          <p className="text-[9px] text-foreground-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Score Adicional */}
                    <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200/50">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                          <i className="ri-add-circle-line"></i> Score Adicional Generado
                        </span>
                        <span className="text-lg font-extrabold text-emerald-600">+{selectedUser.scoreAdicionalGenerado}</span>
                      </div>
                    </div>

                    {/* Access Methods */}
                    <div className="mt-4">
                      <p className="text-[10px] font-bold text-foreground-700 mb-2">Vías de Acceso</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUser.accessMethods.map((am) => {
                          const method = vipAccessMethods.find((m) => m.type === am);
                          if (!method) return null;
                          return (
                            <span key={am} className={`px-2 py-1 rounded-full text-[9px] font-semibold ${method.bgColor} ${method.color}`}>
                              <i className={`${method.icon} mr-1`}></i>
                              {method.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Visibilidad */}
                    <div className="mt-4">
                      <p className="text-[10px] font-bold text-foreground-700 mb-2">Visibilidad Activa</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUser.visibilidadActiva.map((vkey) => {
                          const vis = vipVisibility.find((v) => v.key === vkey);
                          if (!vis) return null;
                          return (
                            <span key={vkey} className={`px-2 py-1 rounded-full text-[9px] font-semibold ${vis.bgColor} ${vis.color}`}>
                              <i className={`${vis.icon} mr-1`}></i>
                              {vis.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Suscripción */}
                    <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-background-100">
                      <i className={`${selectedUser.suscriptor ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-close-circle-fill text-foreground-300'} text-lg`}></i>
                      <span className="text-xs text-foreground-600">
                        {selectedUser.suscriptor ? 'Suscriptor VIP activo' : 'No suscriptor — Acceso por otros medios'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Beneficios View */}
          {activeView === 'beneficios' && (
            <div className="mt-6 space-y-6">
              {/* Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {vipBenefits.map((benefit) => (
                  <div key={benefit.key} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl ${benefit.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${benefit.icon} ${benefit.color} text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 mb-1">{benefit.label}</h4>
                        <p className="text-[11px] text-foreground-500 leading-relaxed mb-2">{benefit.description}</p>
                        <div className="flex items-center gap-1.5">
                          {benefit.tiers.map((tier) => (
                            <TierBadge key={tier} tier={tier} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Visibility */}
              <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-eye-line text-violet-500"></i>
                  Visibilidad VIP en la Plataforma
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {vipVisibility.map((v) => (
                    <div key={v.key} className="p-4 rounded-xl bg-background-100 text-center">
                      <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${v.bgColor}`}>
                        <i className={`${v.icon} ${v.color} text-base`}></i>
                      </div>
                      <p className="text-xs font-bold text-foreground-950 mb-0.5">{v.label}</p>
                      <p className="text-[10px] text-foreground-500">{v.location}</p>
                      <p className="text-[9px] text-foreground-400 leading-relaxed mt-1">{v.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Score Actions */}
              <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-flashlight-line text-violet-500"></i>
                  Los VIP Generan Score Adicional
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {vipScoreActions.map((action) => (
                    <div key={action.key} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl ${action.bgColor} border border-transparent`}>
                      <i className={`${action.icon} ${action.color} text-base`}></i>
                      <p className="text-sm font-extrabold text-foreground-950">+{action.points}</p>
                      <p className="text-[9px] text-foreground-600 font-medium text-center">{action.label}</p>
                      <p className="text-[8px] text-foreground-400 text-center">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Acceso View */}
          {activeView === 'acceso' && (
            <div className="mt-6 space-y-6">
              {/* Access Methods */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {vipAccessMethods.map((method) => (
                  <div key={method.type} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl ${method.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${method.icon} ${method.color} text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 mb-1">{method.label}</h4>
                        <p className="text-[11px] text-foreground-500 leading-relaxed mb-2">{method.description}</p>
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold ${method.bgColor} ${method.color}`}>
                          {method.threshold}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tiers Progression */}
              <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-6">
                  <i className="ri-stack-line text-violet-500"></i>
                  Progresión de Tiers VIP
                </h3>
                <div className="flex items-center justify-between gap-2">
                  {(['plata', 'oro', 'diamante'] as VipTier[]).map((tier, idx) => {
                    const tc = tierConfig[tier];
                    const minScore = tier === 'plata' ? '0' : tier === 'oro' ? '500' : '800';
                    const count = tier === 'diamante' ? vipDashboardStats.diamante : tier === 'oro' ? vipDashboardStats.oro : vipDashboardStats.plata;
                    return (
                      <div key={tier} className="flex-1 flex flex-col items-center gap-1.5">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${tc.badgeBg} border-2 ${tc.borderColor}`}>
                          <span className="text-lg">{tc.emoji}</span>
                        </div>
                        <p className={`text-[10px] font-bold ${tc.textColor}`}>{tc.label}</p>
                        <p className="text-[8px] text-foreground-400">{minScore}+ pts</p>
                        <p className="text-[8px] text-foreground-400">{count} miembros</p>
                        {idx < 2 && (
                          <div className="w-full h-0.5 rounded-full bg-background-200 mt-1"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Steps to become VIP */}
              <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-6">
                  <i className="ri-road-map-line text-violet-500"></i>
                  Cómo Convertirse en VIP
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    { step: '1', icon: 'ri-flashlight-line', title: 'Acumulá Puntos', desc: 'Escuchá radio, participá en cabina, comentá y compartí contenido. Cada acción suma puntos RFW que te acercan al status VIP.' },
                    { step: '2', icon: 'ri-fire-line', title: 'Mantené Actividad', desc: 'La constancia es clave. Entrá todos los días, mantené tu racha activa y participá en los eventos de tu comunidad.' },
                    { step: '3', icon: 'ri-vip-diamond-line', title: 'Activá tu Status', desc: 'Al alcanzar 500 puntos VIP, tu status se activa automáticamente. También podés ser invitado o suscribirte.' },
                  ].map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-violet-100 flex items-center justify-center">
                        <i className={`${item.icon} text-violet-600 text-2xl`}></i>
                      </div>
                      <div className="w-7 h-7 mx-auto -mt-5 mb-3 rounded-full bg-violet-500 flex items-center justify-center relative z-10">
                        <span className="text-xs font-extrabold text-background-50">{item.step}</span>
                      </div>
                      <h4 className="text-sm font-bold text-foreground-950 mb-1">{item.title}</h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center border border-background-200/70">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center">
              <i className="ri-rocket-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">¿Listo para ser VIP?</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              El Club VIP es para los usuarios más comprometidos de RFW. Puntos, actividad, invitaciones o suscripción — hay un camino para cada tipo de oyente. Una vez dentro, la experiencia es completamente diferente.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-violet-500 hover:bg-violet-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/gamificacion"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line mr-1.5"></i>
                Ver Gamificación
              </Link>
              <Link
                to="/embajadores"
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line mr-1.5"></i>
                Embajadores
              </Link>
            </div>
          </div>

          {/* Cross-links */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/top-fans" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <i className="ri-star-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Top Fans</h4>
                  <p className="text-xs text-foreground-500">Ranking de actividad</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/insignias" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center">
                  <i className="ri-award-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Insignias</h4>
                  <p className="text-xs text-foreground-500">Colección de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/eventos" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <i className="ri-calendar-event-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Eventos</h4>
                  <p className="text-xs text-foreground-500">Próximos eventos</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla Final */}
          <div className="mt-8 p-6 rounded-2xl bg-violet-100/70 border border-violet-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-vip-diamond-line text-lg text-white"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              VIP debe sentirse especial.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              No es solo una insignia — es pertenecer al círculo más exclusivo de RFW. Contenido que nadie más ve, eventos privados, concursos premium y visibilidad en toda la plataforma. Ser VIP transforma la experiencia radial.
            </p>
          </div>

        </div>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}